import React from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { Box, Button, Link, Stack, Typography } from '@mui/material';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import Dashboard from './Dashboard';
import ClaimHandler from './ClaimHandler';
import ProviderTermsGate from './ProviderTermsGate';
import BrandScreen from './brand/BrandScreen';
import BrandMark from './brand/BrandMark';
import BrandPill from './brand/BrandPill';
import StoreButtons from './brand/StoreButtons';
import { BRAND, WEBSITE_URL } from '../brand/brand';

/** One numbered step of the "how to become a provider" strip. */
const Step = ({ index, children }) => (
  <Stack direction="row" spacing={1.5} alignItems="flex-start">
    <Box
      aria-hidden
      sx={{
        width: 26,
        height: 26,
        flexShrink: 0,
        borderRadius: '50%',
        backgroundColor: BRAND.purple100,
        color: BRAND.purple700,
        fontSize: '0.8rem',
        fontWeight: 700,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      {index}
    </Box>
    <Typography variant="body2" sx={{ color: BRAND.ink700, pt: 0.35, lineHeight: 1.5 }}>
      {children}
    </Typography>
  </Stack>
);

const DashboardRouter = () => {
  const { providerStatus, providerData, signOut } = useAuth();
  const { t, i18n } = useTranslation();

  // The Firebase account's email already belongs to a different account, so the backend has no
  // usable user row for it. Tell them plainly and offer a way to sign in with the right account.
  if (providerStatus === 'email_conflict') {
    return (
      <BrandScreen maxWidth="sm">
        <Stack alignItems="center" spacing={3}>
          <BrandPill label={t('status.pillProviders')} />
          <BrandMark
            title={t('status.emailConflictTitle')}
            subtitle={t('status.emailConflictBody')}
          />
          <Button variant="contained" onClick={signOut} sx={{ borderRadius: '999px', px: 4 }}>
            {t('common.signOut')}
          </Button>
        </Stack>
      </BrandScreen>
    );
  }

  // No application on file — send them to the app, which is where an application is filed.
  if (providerStatus === 'not_submitted') {
    return (
      <BrandScreen maxWidth="sm">
        <ClaimHandler />
        <Stack alignItems="center" spacing={3}>
          <BrandPill label={t('status.pillProviders')} />
          <BrandMark
            title={t('status.notSubmittedTitle')}
            subtitle={t('status.notSubmittedBody')}
          />

          <Stack
            spacing={1.75}
            sx={{
              width: '100%',
              maxWidth: 420,
              textAlign: 'start',
              backgroundColor: BRAND.lavenderBg,
              border: `1px solid ${BRAND.line}`,
              borderRadius: '18px',
              p: 2.5,
            }}
          >
            <Typography
              sx={{ fontSize: '0.78rem', fontWeight: 700, color: BRAND.purple700, letterSpacing: '0.04em' }}
            >
              {t('status.stepsTitle')}
            </Typography>
            <Step index={1}>{t('status.step1')}</Step>
            <Step index={2}>{t('status.step2')}</Step>
            <Step index={3}>{t('status.step3')}</Step>
          </Stack>

          <Stack spacing={1.25} alignItems="center" sx={{ width: '100%' }}>
            <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, color: BRAND.ink700 }}>
              {t('status.downloadApp')}
            </Typography>
            <StoreButtons />
          </Stack>

          <Typography variant="body2" sx={{ color: BRAND.ink500 }}>
            <Link
              href={WEBSITE_URL}
              target="_blank"
              rel="noopener noreferrer"
              sx={{ color: BRAND.purple700, fontWeight: 600, textDecoration: 'none' }}
            >
              {t('status.learnMore')}
            </Link>
          </Typography>
        </Stack>
      </BrandScreen>
    );
  }

  // Application filed, awaiting the moderation queue.
  if (providerStatus === 'submitted') {
    return (
      <BrandScreen maxWidth="sm">
        <Stack alignItems="center" spacing={3}>
          <BrandPill label={t('status.pillUnderReview')} tone="pending" />
          <BrandMark
            title={t('status.underReviewTitle')}
            subtitle={t('status.underReviewBody')}
          />
          <Stack
            direction="row"
            spacing={1.25}
            alignItems="center"
            sx={{
              backgroundColor: '#FFFBEB',
              border: '1px solid #FDE68A',
              borderRadius: '14px',
              px: 2,
              py: 1.5,
            }}
          >
            <HourglassEmptyIcon sx={{ fontSize: 20, color: '#B45309' }} />
            <Typography variant="body2" sx={{ color: '#92400E', fontWeight: 500 }}>
              {t('status.underReviewThanks')}
            </Typography>
          </Stack>
        </Stack>
      </BrandScreen>
    );
  }

  // Approved - show dashboard, behind the Provider Terms gate.
  // Wraps ONLY this branch: a provider is known to exist here, so the gate's terms-status call
  // cannot hit the no-provider 404 path.
  if (providerStatus === 'approved' && providerData) {
    return (
      <ProviderTermsGate locale={i18n.language}>
        <Dashboard providerData={providerData} />
      </ProviderTermsGate>
    );
  }

  // Loading or unknown state
  return null;
};

export default DashboardRouter;
